import VersionDisplay from './VersionDisplay';

const Sidebar = ({ 
  version, 
  setVersion, 
  handleOpenClick, 
  handleSave, 
  isMA3, 
  onKeywordsUpdate, 
  onClose,
  onOpenLibrary 
}) => {
  const theme = isMA3 ? 'ma3' : 'ma2';
  const bgColor = isMA3 ? 'bg-ma3-surface' : 'bg-ma2-surface';
  const textColor = isMA3 ? 'text-ma3-text' : 'text-ma2-text';
  const accentColor = isMA3 ? 'text-ma3-accent' : 'text-ma2-accent';
  const buttonBg = isMA3 ? 'bg-ma3-accent text-white hover:bg-blue-600' : 'bg-ma2-accent text-black hover:bg-yellow-500';

  return (
    <div className={`
      flex flex-col p-4 xl-custom:p-6 w-full h-full
      ${bgColor} ${textColor}
      border-r border-opacity-10 border-gray-500
      overflow-y-auto custom-scrollbar
    `}>
      {/* Branding */}
      <div className="flex items-center justify-between mb-6 xl-custom:mb-8">
        <h1 className={`text-2xl font-bold tracking-tight ${accentColor} flex items-center gap-2`}>
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
          </svg>
          MACRO<span className={isMA3 ? "text-gray-800" : "text-white"}>DESK</span>
        </h1>
        
        {/* Mobile Close Button */}
        <button 
          onClick={onClose}
          className={`xl-custom:hidden p-2 rounded-lg ${isMA3 ? 'hover:bg-gray-200' : 'hover:bg-white/10'}`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Primary Actions */}
      <div className="mb-6 xl-custom:mb-8 xl-custom:hidden">
        <label className="text-xs font-medium opacity-50 uppercase tracking-wider mb-3 block">Navigation</label>
        <div className="flex flex-col gap-3">
          <button 
            onClick={() => {
              onOpenLibrary();
            }}
            className={`
              py-3 px-4 rounded-xl font-semibold shadow-lg transform transition-transform duration-200 hover:scale-[1.02] active:scale-95
              flex items-center justify-center gap-2
              ${isMA3 ? 'bg-blue-50 text-blue-600 border border-blue-100' : 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'}
            `}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Library
          </button>
        </div>
      </div>

      {/* File Actions */}
      <div className="mb-6 xl-custom:mb-8">
        <label className="text-xs font-medium opacity-50 uppercase tracking-wider mb-3 block">File Operations</label>
        <div className="flex flex-col gap-3">
          <button 
            onClick={handleOpenClick}
            className={`
              py-3 px-4 rounded-xl font-semibold shadow-lg transform transition-transform duration-200 hover:scale-[1.02] active:scale-95
              flex items-center justify-center gap-2
              ${isMA3 ? 'bg-white text-gray-800 border border-gray-200 hover:border-blue-500' : 'bg-[#2A2A2A] text-white hover:bg-[#333] border border-transparent'}
            `}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 19a2 2 0 01-2-2V7a2 2 0 012-2h4l2 2h4a2 2 0 012 2v1M5 19h14a2 2 0 002-2v-5a2 2 0 00-2-2H9a2 2 0 00-2 2v5a2 2 0 01-2 2z" /></svg>
            Open File
          </button>
          <button 
            onClick={handleSave}
            className={`
              py-3 px-4 rounded-xl font-semibold shadow-lg transform transition-transform duration-200 hover:scale-[1.02] active:scale-95
              flex items-center justify-center gap-2
              ${buttonBg}
            `}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-4 4m0 0l-4-4m4 4V4" /></svg>
            Save XML
          </button>
        </div>
      </div>

      {/* Version Toggle */}
      <div className="mb-6 xl-custom:mb-8">
        <label className="text-xs font-medium opacity-50 uppercase tracking-wider mb-3 block">System Version</label>
        <div 
          className={`
            relative p-1 rounded-xl h-14 flex items-center select-none border
            transition-all duration-300
            ${isMA3 ? 'bg-gray-200 border-gray-300' : 'bg-white/10 border-white/5'}
          `}
        >
           <div className={`
              absolute top-1 bottom-1 rounded-lg shadow-sm
              transition-all duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]
              ${isMA3 ? 'left-1/2 right-1 bg-white' : 'left-1 right-1/2 bg-zinc-800'}
           `}></div>
          
          <div 
            onClick={() => setVersion("MA2")}
            className={`
              flex-1 text-center z-10 text-sm font-bold transition-all cursor-pointer h-full flex items-center justify-center
              ${!isMA3 ? 'text-yellow-400' : 'text-gray-500 hover:text-gray-700'}
            `}
          >
            MA 2
          </div>
          <div 
            onClick={() => setVersion("MA3")}
            className={`
              flex-1 text-center z-10 text-sm font-bold transition-all cursor-pointer h-full flex items-center justify-center
              ${isMA3 ? 'text-blue-600' : 'text-gray-500 hover:text-white/70'}
            `}
          >
            MA 3
          </div>
        </div>
      </div>

      <div className="mb-auto">
         <VersionDisplay isMA3={isMA3} onKeywordsUpdate={onKeywordsUpdate} />
      </div>


      <div className="mt-8 text-xs opacity-30">
        <p>© 2025-{new Date().getFullYear()} Oleksandr Zhuikov</p>
        <p>v1.0.0</p>
      </div>
    </div>
  );
};

export default Sidebar;
